/**
 * Liveness Detection Helper Functions
 * Simple face and blink detection without external libraries
 */

/**
 * Detect if face is present in the circular region
 * @param {ImageData} imageData - Canvas image data
 * @param {number} width - Canvas width
 * @param {number} height - Canvas height
 * @returns {Object} Detection result with confidence
 */
export const detectFaceInCircle = (imageData, width, height) => {
    const data = imageData.data;
    
    const centerX = width / 2;
    const centerY = height / 2;
    const radius = Math.min(width, height) * 0.3;

    let skinPixelCount = 0;
    let totalPixels = 0;

    // Scan circular region
    for (let y = Math.max(0, centerY - radius); y < Math.min(height, centerY + radius); y++) {
        for (let x = Math.max(0, centerX - radius); x < Math.min(width, centerX + radius); x++) {
            const dx = x - centerX;
            const dy = y - centerY;
            const distance = Math.sqrt(dx * dx + dy * dy);

            if (distance <= radius) {
                const idx = (y * width + x) * 4;
                const r = data[idx];
                const g = data[idx + 1];
                const b = data[idx + 2];
                
                // Simplified skin tone detection
                const isSkinTone = r > 95 && g > 40 && b > 20 && 
                                  r > g && r > b &&
                                  Math.abs(r - g) > 15;

                if (isSkinTone) {
                    skinPixelCount++;
                }
                totalPixels++;
            }
        }
    }

    const faceRatio = skinPixelCount / totalPixels;
    const faceDetected = faceRatio > 0.3 && faceRatio < 0.8;

    return {
        faceDetected,
        confidence: faceRatio,
        skinPixelCount,
        totalPixels
    };
};

/**
 * Simple blink detection using brightness analysis
 * @param {ImageData} imageData - Canvas image data
 * @param {number} width - Canvas width
 * @param {number} height - Canvas height
 * @returns {boolean} True if eyes appear closed
 */
export const detectEyesClosed = (imageData, width, height) => {
    const data = imageData.data;
    
    const centerX = width / 2;
    const centerY = height / 2;
    const radius = Math.min(width, height) * 0.3;

    let darkPixelCount = 0;
    let eyeRegionPixels = 0;

    // Scan upper region where eyes should be
    const eyeRegionTop = centerY - radius * 0.6;
    const eyeRegionBottom = centerY - radius * 0.2;

    for (let y = Math.max(0, eyeRegionTop); y < Math.min(height, eyeRegionBottom); y++) {
        for (let x = Math.max(0, centerX - radius); x < Math.min(width, centerX + radius); x++) {
            const dx = x - centerX;
            const dy = y - centerY;
            const distance = Math.sqrt(dx * dx + dy * dy);

            if (distance <= radius) {
                const idx = (y * width + x) * 4;
                const r = data[idx];
                const g = data[idx + 1];
                const b = data[idx + 2];
                const brightness = (r + g + b) / 3;

                if (brightness < 80) {
                    darkPixelCount++;
                }
                eyeRegionPixels++;
            }
        }
    }

    const darkRatio = darkPixelCount / eyeRegionPixels;
    return darkRatio < 0.05; // Very few dark pixels = eyes closed
};

/**
 * Draw face detection circle overlay
 * @param {CanvasRenderingContext2D} ctx - Canvas context
 * @param {Object} detection - Detection result object
 */
export const drawFaceCircle = (ctx, detection) => {
    const width = ctx.canvas.width;
    const height = ctx.canvas.height;
    const centerX = width / 2;
    const centerY = height / 2;
    const radius = Math.min(width, height) * 0.3;

    const faceDetected = detection && detection.faceDetected;

    ctx.strokeStyle = faceDetected ? '#00ff00' : '#ffffff';
    ctx.lineWidth = 4;
    ctx.shadowBlur = faceDetected ? 15 : 5;
    ctx.shadowColor = faceDetected ? '#00ff00' : 'rgba(255, 255, 255, 0.5)';

    ctx.beginPath();
    ctx.arc(centerX, centerY, radius, 0, 2 * Math.PI);
    ctx.stroke();
    
    ctx.shadowBlur = 0;
};

/**
 * Validate recorded video
 * @param {Blob} videoBlob - Recorded video blob
 * @returns {Object} Validation result
 */
export const validateLivenessVideo = async (videoBlob) => {
    if (!videoBlob || videoBlob.size === 0) {
        return { valid: false, error: 'No video data' };
    }

    // Minimum size check (should be at least 100KB for 5 seconds)
    if (videoBlob.size < 100000) {
        return { valid: false, error: 'Video too short or corrupted' };
    }

    return { 
        valid: true, 
        size: videoBlob.size,
        duration: 5000 // We know it's 5 seconds
    };
};
